import * as HLF from "html-living-framework";
import {Application} from "../Application";
import {Anchor} from "../core/Anchor";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {Rect} from "../core/Shape";
import {Times} from "../core/Signal";
import {Assets} from "../data/Assets";
import {BookId} from "../data/BookId";
import {Button} from "../gameobjects/Button";
import {Focusables} from "../gameobjects/Focusables";
import {InteractiveSceneItem} from "../gameobjects/InteractiveSceneItem";
import {MuteButton} from "../gameobjects/MuteButton";
import {Optionable} from "../gameobjects/Optionable";
import {SceneItemButton} from "../gameobjects/SceneItemButton";
import {StoryScene} from "../gameobjects/StoryScene";
import {StorySceneOptions} from "../gameobjects/StorySceneOptions";
import {AudioChannel} from "../managers/AudioChannel";
import {GameManager} from "../managers/GameManager";
import {PlayMode, VoiceOverManager} from "../managers/VoiceOverManager";
import {PaperBackgroundState} from "./PaperBackgroundState";

export class StoryCreator extends PaperBackgroundState {

    public static readonly NAME = "StoryCreator";

    public options?: StorySceneOptions;
    public scene?: StoryScene;

    private readonly gameManager = GameManager.instance;
    private readonly container = this.addChild(new Container());
    private readonly buttonNext = this.addChild(new Button(this.onNextScene.bind(this), "button-continue"));
    private readonly buttonPrev = this.addChild(new Button(this.onPrevScene.bind(this), "button-back-yellow"));
    private readonly buttonMute = this.addChild(new MuteButton());

    private _idleTimeout?: number;
    private _lastIdleVO?: string;

    public init(pSize: PIXI.Point) {
        super.init(pSize);

        this.createOptions();

        // Removed the back button
        this.buttonPrev.visible = false;
        this.buttonPrev.moveYFrom(-this._size.y - this.buttonPrev.height * 0.5, 0.4, Ease.backOut);
        this.loadSfx();

        AudioChannel.BGM.play(Assets.audio.bgmStory.assetName, "loop");
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);

        this.buttonNext.position.set(
            this._size.x / 2 - this.buttonNext.width * 0.6,
            this._size.y / 2 - this.buttonNext.height * 0.6);
        this.buttonPrev.position.set(
            -this._size.x / 2 + this.buttonPrev.width * 0.6,
            -this._size.y / 2 + this.buttonPrev.height * 0.6);
        this.buttonMute.setPosition(pSize);
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions): void {
        this.unloadSfx();
        AudioChannel.BGM.stop(Assets.audio.bgmStory.assetName);
        AudioChannel.BGM.volume = 1;
        super.destroy(pOptions);
    }

    private loadSfx() {
        // All audio is preloaded
        // const storySfx = StoryAudioAssets(GameManager.instance.story);
        // if (storySfx) {
        //     AudioChannel.SFX.load(storySfx.map((it) => it.assetName));
        // }
    }

    private unloadSfx() {
        // All audio is preloaded
        // const storySfx = StoryAudioAssets(GameManager.instance.story);
        // if (storySfx) {
        //     AudioChannel.SFX.unload(storySfx.map((it) => it.assetName));
        // }
    }

    private createOptions() {
        this.destroyScene();
        this.options = this.container.addChild(new StorySceneOptions(this.gameManager.currentScene, this._size));
        this.options!.onSelect.add((option) => {
            this.gameManager.selectOption(option.optionId);
            this.createScene();
        });
        this.buttonNext.visible = false;
        Focusables.add(
            this.buttonPrev,
            this.buttonMute,
            this.options!.caption,
        );

        const vo = this.gameManager.getCurrentScenePromptVO();
        if (vo) {
            this.options!.caption.initVO(vo);
        }

        AudioChannel.BGM.volume = 1;
    }

    private createScene() {
        this.destroyScene();
        this.scene = this.container.addChild(new StoryScene(this.gameManager.currentScene, this._size));
        this.buttonNext.visible = false;
        this.buttonNext.setLayoutTexture(this.gameManager.isLastScene() ? "button-check" : "button-continue", 0.5, 0.5);
        Focusables.add(
            this.buttonPrev,
            this.buttonMute,
            this.scene!.caption,
        );

        const onCompleteAnimationAndVO = Times(2, () => {
            this.showButtonNext();
        });

        this.scene!.interactiveChildren = false;
        this.scene!.animate(() => {
            const sceneSlotButtons = this.scene!.collectFromItems((it: Optionable, buttons: SceneItemButton[]) => {
                if (it instanceof InteractiveSceneItem && it.button) {
                    buttons.push(it.button);
                }
            });
            this.scene!.interactiveChildren = true;
            Focusables.add(...sceneSlotButtons);
            onCompleteAnimationAndVO();
        });

        const vo = this.gameManager.getCurrentSceneAnswerVO(this.gameManager.getCurrentSceneSelectedOptionIndex());
        if (vo) {
            this.scene!.caption.show();
            this.scene!.caption.initVO(vo, 8, () => {
                if (this.gameManager.currentScene.injections && this.gameManager.currentScene.injections.afterFullVO) {
                    this.gameManager.currentScene.injections.afterFullVO.execute(this, () => {
                        onCompleteAnimationAndVO();
                        this.startIdleInterval();
                    });
                } else {
                    onCompleteAnimationAndVO();
                    this.startIdleInterval();
                }
            });
        } else {
            this.scene!.caption.show(onCompleteAnimationAndVO);
        }
        if (Application.instance.audio.masterVolume === 0) {
            onCompleteAnimationAndVO();
            onCompleteAnimationAndVO();
        }

        const sfx = this.gameManager.getCurrentSceneSfx();
        if (sfx) {
            const volume = 1;
            if (typeof sfx === "string") {
                AudioChannel.SFX.play(sfx, volume);
            } else if (Array.isArray(sfx)) {
                // todo support delay between sfx
                AudioChannel.SFX.play(sfx.filter((it) => typeof it === "string") as string[], volume);
            } else {
                for (const p of Object.getOwnPropertyNames(sfx)) {
                    const delay = sfx[p];
                    if (typeof delay === "number") {
                        this.scene!.delayedCall(delay, () => {
                            AudioChannel.SFX.play(p, volume);
                        });
                    } else if (delay) {
                        for (const d of delay) {
                            this.scene!.delayedCall(d, () => {
                                AudioChannel.SFX.play(p, volume);
                            });
                        }
                    } else {
                        AudioChannel.SFX.play(p, volume);
                    }
                }
            }
        }

        AudioChannel.BGM.volume = 0;
    }

    private showButtonNext() {
        if (!this.buttonNext.visible) {
            this.buttonNext.visible = true;
            this.buttonNext.moveYFrom(this._size.y / 2 + this.buttonNext.height, 0.4, Ease.backOut);
            Focusables.add(this.buttonNext);
        }
    }

    private destroyScene() {
        this.container.destroyChildren();
        this.options = undefined;
        this.scene = undefined;
        Focusables.clear();
        VoiceOverManager.instance.stopVO();
        AudioChannel.SFX.stop();
        this.stopIdleInterval();
    }

    private onNextScene() {
        if (this.gameManager.nextScene()) {
            this.createOptions();
        } else {
            this.fadeOut(true);
        }
    }

    private onPrevScene() {
        if (this.options) {
            if (this.gameManager.prevScene()) {
                this.createScene();
            } else {
                this.fadeOut(false);
            }
        } else if (this.scene) {
            this.createOptions();
        }
    }

    private fadeOut(storyFinished: boolean) {
        this.lockScreen();
        this.stopIdleInterval();
        const overlay = this.addChild(new Rect(this._size.x, this._size.y, 0xffffff, 1, Anchor.middleCenter));
        overlay.zIndex = this.buttonMute.zIndex;
        overlay.alphaFrom(0, 0.4, () => {
            this.unlockScreen();
            this.gotoHomeState({
                openBookId: this.gameManager.story.storyId as BookId,
                storyFinished,
                sceneRender: storyFinished ? this.renderLastScene() : undefined,
            });
        });
    }

    private renderLastScene() {
        const w = this._size.y * 4 / 3;
        const h = this._size.y;
        const scene = new StoryScene(this.gameManager.currentScene, this._size);
        const texture = PIXI.RenderTexture.create(w, h);
        const m = new PIXI.Matrix();
        m.translate(w / 2, h / 2);
        scene.caption.visible = false;
        Application.instance.renderer.render(scene, texture, true, m);
        scene.destroy();
        return texture;
    }

    private startIdleInterval() {
        this.stopIdleInterval();
        this._idleTimeout = window.setInterval(() => {
            const vos = this.gameManager.isLastScene() ?
                Assets.audio.storyIdle.map((it) => it.assetName).filter((it) => it.indexOf("001") < 0) :
                Assets.audio.storyIdle.map((it) => it.assetName);
            const vo = HLF.ArrayUtils.random(vos.filter((it) => it !== this._lastIdleVO));
            this._lastIdleVO = vo;
            VoiceOverManager.instance.playVO(vo, PlayMode.New, () => {
                this.startIdleInterval();
            });
        }, 15000);
    }

    private stopIdleInterval() {
        if (this._idleTimeout !== undefined) {
            clearTimeout(this._idleTimeout);
            this._idleTimeout = undefined;
            this._lastIdleVO = undefined;
        }
    }
}
